import { useState } from "react";
import { SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { conciergeEnabled, requestPlan } from "@/onboarding/concierge/api";
import { trackOnboarding } from "@/onboarding/analytics";
import type { ProvisioningPlan } from "@/onboarding/concierge/types";

export type DescribeOutcome = "watchlist" | "filter";

interface DescribeItBarProps {
  /** Called with the plan when it proposes resources to build. */
  onWatchlistPlan: (plan: ProvisioningPlan) => void;
  onFilterPlan: (plan: ProvisioningPlan) => void;
}

/**
 * Natural-language entry point on the Watchlists & Filters page: describe
 * the desired outcome, pick whether it's a highlight watchlist or a filtered
 * view, and the concierge checks the Newsquawk library first, then drafts a
 * personal version into the normal builder for review. Rendered only when
 * VITE_CONCIERGE_ENABLED is on.
 */
export default function DescribeItBar({
  onWatchlistPlan,
  onFilterPlan,
}: DescribeItBarProps) {
  const [text, setText] = useState("");
  const [outcome, setOutcome] = useState<DescribeOutcome>("watchlist");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<{
    headline: string;
    bullets: string[];
  } | null>(null);
  // Held until the user clicks "Review draft" — the summary explains the
  // plan (library reuse etc.) and must be readable BEFORE a modal covers it.
  const [pendingPlan, setPendingPlan] = useState<{
    plan: ProvisioningPlan;
    outcome: DescribeOutcome;
  } | null>(null);

  if (!conciergeEnabled) return null;

  const handleBuild = async () => {
    const query = text.trim();
    if (query.length < 3 || loading) return;
    setLoading(true);
    setNotice(null);
    trackOnboarding("concierge_requested", `builder_${outcome}`);
    try {
      const response = await requestPlan(query, outcome);
      const plan = response.plan;
      const proposesSomething =
        outcome === "watchlist"
          ? plan.portfolios.length > 0 || plan.tags_watchlists.length > 0
          : plan.headline_queries.length > 0;
      // Belt-and-braces: strip any lib:/libq: ref syntax that slips into
      // user-facing copy (the prompt forbids it).
      const strip = (text: string) => text.replace(/\blibq?:/g, "");
      setNotice({
        headline: strip(plan.headline),
        bullets: plan.bullets.map(strip),
      });
      if (proposesSomething) {
        // Don't open the builder over the explanation — the user reads the
        // summary first, then opens the draft deliberately.
        setPendingPlan({ plan, outcome });
        setText("");
      } else {
        setPendingPlan(null);
      }
    } catch (error) {
      console.error("[concierge] describe-it failed:", error);
      toast.error("Couldn't build that right now — try again or use the manual builder.");
    } finally {
      setLoading(false);
    }
  };

  const chip = (value: DescribeOutcome, label: string) => (
    <button
      type="button"
      onClick={() => setOutcome(value)}
      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer border ${
        outcome === value
          ? "border-squawk-blue bg-squawk-blue/15 text-squawk-blue"
          : "border-gray-300 dark:border-gray-600 bg-transparent text-gray-500 dark:text-gray-400 hover:border-gray-400"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mb-2">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2">
        <SparklesIcon className="hidden sm:block h-4 w-4 shrink-0 text-squawk-blue" />
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleBuild();
          }}
          placeholder='Describe what you want to monitor — e.g. "anything OPEC says" or "only important UK inflation news"'
          maxLength={500}
          className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
        />
        <div className="flex items-center gap-2 shrink-0">
          {chip("watchlist", "Highlight words (watchlist)")}
          {chip("filter", "Filtered view (advanced filter)")}
          <button
            type="button"
            onClick={handleBuild}
            disabled={loading || text.trim().length < 3}
            className="rounded-md bg-squawk-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-squawk-blue/90 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {loading ? "Building…" : "Build"}
          </button>
        </div>
      </div>

      {loading && (
        <p className="mt-1.5 px-1 text-xs text-gray-500 animate-pulse">
          Checking the Newsquawk library and drafting…
        </p>
      )}

      {notice && !loading && (
        <div className="mt-1.5 flex items-start gap-2 rounded-md border border-squawk-blue/30 bg-squawk-blue/5 px-3 py-2">
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
              {notice.headline}
            </p>
            {notice.bullets.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {notice.bullets.map((bullet) => (
                  <li
                    key={bullet}
                    className="flex items-start gap-1.5 text-xs leading-snug text-gray-600 dark:text-gray-400"
                  >
                    <span className="mt-[6px] h-1 w-1 shrink-0 rounded-full bg-squawk-blue" />
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {pendingPlan && (
            <button
              type="button"
              onClick={() => {
                if (pendingPlan.outcome === "watchlist") {
                  onWatchlistPlan(pendingPlan.plan);
                } else {
                  onFilterPlan(pendingPlan.plan);
                }
              }}
              className="shrink-0 rounded-md bg-squawk-blue px-2.5 py-1 text-xs font-semibold text-white hover:bg-squawk-blue/90 transition-colors cursor-pointer"
            >
              Review draft →
            </button>
          )}
          <button
            type="button"
            onClick={() => { setNotice(null); setPendingPlan(null); }}
            aria-label="Dismiss"
            className="bg-transparent border-none p-0 text-gray-400 hover:text-gray-200 cursor-pointer"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
